import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Trophy, Star, RotateCcw, Home, CheckCircle2, Target, BookOpen } from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";

export default function DailyReflectionResult() {
  const { lang } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("dr_result");
      if (!raw) { navigate("/daily-reflection", { replace: true }); return; }
      setData(JSON.parse(raw));
    } catch (_) {
      navigate("/daily-reflection", { replace: true });
    }
  }, [navigate]);

  const encouragementKey = useMemo(() => {
    if (!data) return "dr_encouragement_mid";
    const score = data.total_score || 0;
    if (score >= 40) return "dr_encouragement_high";
    if (score >= 30) return "dr_encouragement_mid";
    return "dr_encouragement_low";
  }, [data]);

  if (!data) return null;
  const { total_score, max_score, per_question, session_questions = [] } = data;
  const questionsByRow = Object.fromEntries(session_questions.map((q) => [q.row_index, q]));

  return (
    <div className="page-shell" data-testid="dr-result-page">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="p-2 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
          data-testid="dr-result-back"
          aria-label="back"
        >
          <ArrowLeft size={18} strokeWidth={2.5} className="text-slate-600" />
        </button>
        <span className="font-heading font-bold text-sm text-white drop-shadow-[0_2px_8px_rgba(15,23,42,0.5)] truncate">
          {t(lang, "daily_reflection")}
        </span>
        <span className="w-9" />
      </div>

      {/* Score hero */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 140, damping: 14 }}
        className="mt-6 relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-amber-400 p-6 text-white border-4 border-white shadow-[0_8px_0_rgba(0,0,0,0.15)]"
        data-testid="dr-score-card"
      >
        <div className="absolute -top-10 -right-8 opacity-20">
          <Trophy size={140} strokeWidth={1.5} />
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 bg-white/25 backdrop-blur rounded-full px-3 py-1 text-xs font-heading font-bold uppercase tracking-wider">
            <Star size={12} strokeWidth={3} /> {t(lang, "dr_growth_score")}
          </div>
          <div className="mt-3 flex items-end gap-1">
            <span className="font-heading text-6xl font-bold leading-none" data-testid="dr-score-value">
              {total_score}
            </span>
            <span className="font-heading text-xl font-bold text-white/80 mb-1">/ {max_score}</span>
          </div>
          <p className="mt-3 text-sm font-semibold leading-snug text-white/95" data-testid="dr-encouragement">
            {t(lang, encouragementKey)}
          </p>
        </div>
      </motion.div>

      {/* Per-question review */}
      <div className="mt-6 flex flex-col gap-4" data-testid="dr-review">
        {per_question.map((item, i) => {
          const q = questionsByRow[item.row_index] || {};
          const yourIsBest = item.chosen_letter === item.best_letter;
          return (
            <motion.div
              key={item.row_index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i }}
              className="bg-white rounded-3xl p-5 border-4 border-white shadow-[0_6px_0_rgba(0,0,0,0.08)]"
              data-testid={`dr-review-item-${i}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-700 px-2.5 py-1 rounded-full text-[11px] font-heading font-bold uppercase tracking-wider">
                  <Sparkles size={11} strokeWidth={3} />
                  {q.category || ""}
                </span>
                {yourIsBest && (
                  <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full text-[11px] font-heading font-bold uppercase tracking-wider">
                    <CheckCircle2 size={11} strokeWidth={3} />
                  </span>
                )}
              </div>
              <p className="mt-3 font-heading text-base font-bold text-slate-800 leading-snug">
                {q.question}
              </p>

              <ReviewRow
                icon={<Target size={14} strokeWidth={2.75} />}
                label={t(lang, "dr_your_answer")}
                text={`${item.chosen_letter}. ${item.chosen_text}`}
                good={yourIsBest}
              />
              {!yourIsBest && (
                <ReviewRow
                  icon={<CheckCircle2 size={14} strokeWidth={2.75} />}
                  label={t(lang, "dr_best_answer")}
                  text={`${item.best_letter}. ${item.best_text}`}
                  good
                />
              )}
              {item.lesson && (
                <ReviewRow
                  icon={<BookOpen size={14} strokeWidth={2.75} />}
                  label={t(lang, "dr_lesson")}
                  text={item.lesson}
                  isLesson
                />
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <button
          onClick={() => { sessionStorage.removeItem("dr_result"); navigate("/daily-reflection"); }}
          className="bg-white border-2 border-slate-900/10 text-slate-700 font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
          data-testid="dr-result-try-again-btn"
        >
          <RotateCcw size={16} strokeWidth={2.75} className="shrink-0" />
          <span>{t(lang, "dr_try_again")}</span>
        </button>
        <button
          onClick={() => navigate("/")}
          className="bg-violet-500 hover:bg-violet-600 text-white font-heading font-bold uppercase tracking-tight text-xs py-4 px-2 rounded-full btn-squish inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
          data-testid="dr-result-home-btn"
        >
          <Home size={16} strokeWidth={2.75} className="shrink-0" />
          <span>{t(lang, "home")}</span>
        </button>
      </div>
    </div>
  );
}

function ReviewRow({ icon, label, text, good = false, isLesson = false }) {
  const palette = isLesson
    ? "bg-amber-50 border-amber-200 text-amber-900"
    : good
    ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : "bg-rose-50 border-rose-200 text-rose-900";
  const iconBg = isLesson ? "bg-amber-400 text-white" : good ? "bg-emerald-500 text-white" : "bg-rose-500 text-white";
  return (
    <div className={"mt-3 rounded-2xl border-2 p-3 flex items-start gap-2 " + palette}>
      <span className={"shrink-0 w-7 h-7 rounded-full flex items-center justify-center " + iconBg}>
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-heading font-bold uppercase tracking-wider opacity-80">{label}</p>
        <p className="text-sm font-semibold leading-snug mt-0.5">{text}</p>
      </div>
    </div>
  );
}
